import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  pointerWithin,
  DragOverlay,
  closestCorners,
} from '@dnd-kit/core';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import { arrayMove } from '@dnd-kit/sortable';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import api from '../api';
import SortableItem from './SortableItem';
import KanbanColumn from './KanbanColumn';
import { PageHeader } from '../components/common';
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Spinner } from "../components/ui/spinner";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { PlusCircle, Search } from "lucide-react";
import NegotiationModal from './NegotiationModal';

export default function KanbanBoard() {
  const { t } = useTranslation();
  const [negotiations, setNegotiations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedNegotiation, setSelectedNegotiation] = useState(null);
  const [searchPlate, setSearchPlate] = useState('');
  const [activeId, setActiveId] = useState(null);

  // Mapeamento entre os estágios do backend e as colunas do Kanban
  const statusColumnMapping = useMemo(() => ({
    'recebido': 'new',
    'negociacao': 'negotiating',
    'fechar': 'contacted',
    'ganho': 'closed',
    'perdido': 'archived'
  }), []);

  // Mapeamento inverso para uso no handleDragEnd
  const columnStageMapping = useMemo(() => 
    Object.entries(statusColumnMapping).reduce((acc, [stage, status]) => {
      acc[status] = stage;
      return acc;
    }, {}),
  [statusColumnMapping]);

  const loadNegotiations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/negotiations');
      
      if (response.data && Array.isArray(response.data)) {
        const mappedNegotiations = response.data.map(negotiation => ({
          ...negotiation,
          status: statusColumnMapping[negotiation.stage] || 'new'
        }));
        
        setNegotiations(mappedNegotiations);
      } else {
        setNegotiations([]);
      }
    } catch (error) {
      console.error('Error loading negotiations:', error);
    } finally {
      setLoading(false);
    }
  }, [statusColumnMapping]);

  useEffect(() => {
    loadNegotiations();
  }, [loadNegotiations]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Menor distância para ativar = mais responsivo
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Função para filtrar negociações por placa
  const filteredNegotiations = useMemo(() => {
    if (!searchPlate.trim()) return negotiations;
    
    return negotiations.filter(negotiation => {
      // Verifica se algum veículo da negociação contém a placa pesquisada
      return negotiation.vehicles?.some(vehicle => 
        vehicle.placa?.toLowerCase().includes(searchPlate.toLowerCase())
      );
    });
  }, [negotiations, searchPlate]);

  const handleOpenModal = (negotiationId = null) => {
    setSelectedNegotiation(negotiationId);
    setIsModalOpen(true);
  };

  const handleCloseModal = (shouldRefresh = false) => {
    setIsModalOpen(false);
    setSelectedNegotiation(null);
    if (shouldRefresh) {
      loadNegotiations();
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  // Função para identificar a coluna de um card
  const findColumnOfCard = (cardId) => {
    const card = negotiations.find(n => n._id === cardId);
    return card ? card.status : null;
  };

  // Função para determinar a coluna sobre a qual o item está
  const findColumnFromPoint = (x, y) => {
    const elements = document.elementsFromPoint(x, y);
    
    for (const el of elements) {
      const columnId = el.getAttribute('data-column-id');
      if (columnId) {
        return columnId;
      }
    }
    return null;
  };
  
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    
    if (!active || !over) return;

    const activeItem = negotiations.find(item => item._id === active.id);
    if (!activeItem) return;
    
    // Extract the column id from the over.id if it's a column drop zone
    let targetColumnId;
    
    if (over.id && typeof over.id === 'string' && over.id.startsWith('column-')) {
      // This means we're dropping directly onto a column
      targetColumnId = over.id.replace('column-', '');
      console.log(`Dropped directly onto column: ${targetColumnId}`);
    } else {
      // If not dropped directly on column, check the data from over
      if (over.data && over.data.current) {
        if (over.data.current.type === 'column') {
          // This is a column's droppable area
          targetColumnId = over.data.current.column.id;
          console.log(`Dropped on column droppable: ${targetColumnId}`);
        } else if (over.data.current.type === 'item') {
          // This is another item, get its column
          targetColumnId = over.data.current.columnId;
          console.log(`Dropped on item in column: ${targetColumnId}`);
        }
      }
      
      // If we still don't have a column ID, try to find from the DOM
      if (!targetColumnId && event.activatorEvent) {
        targetColumnId = findColumnFromPoint(
          event.activatorEvent.clientX, 
          event.activatorEvent.clientY
        );
        console.log(`Found column from point: ${targetColumnId}`);
      }
    }
    
    console.log("Drag end - Active item:", activeItem._id);
    console.log("Drag end - Target column:", targetColumnId);
    
    if (!targetColumnId) {
      console.error("Could not determine target column");
      return;
    }

    const targetColumn = columns.find(col => col.id === targetColumnId);
    if (!targetColumn) {
      console.error(`Target column not found: ${targetColumnId}`);
      return;
    }

    // Se arrastou para uma coluna diferente
    if (activeItem.status !== targetColumn.id) {
      console.log(`Moving card ${activeItem._id} from ${activeItem.status} to ${targetColumn.id}`);
      
      const newStage = targetColumn.stage;
      
      // Update the local state first for immediate UI feedback
      const updatedNegotiations = negotiations.map(item =>
        item._id === active.id
          ? { ...item, status: targetColumn.id, stage: newStage }
          : item
      );
      
      setNegotiations(updatedNegotiations);
      
      try {
        // Then update the backend
        const response = await api.patch(`/negotiations/${active.id}/move`, {
          stage: newStage
        });
        console.log("API call successful - card moved to new column");
        
        // Update the state with the data returned from the API (with populated vehicles)
        if (response.data) {
          const updatedWithApiData = negotiations.map(item =>
            item._id === active.id
              ? { ...response.data, status: targetColumn.id }
              : item
          );
          setNegotiations(updatedWithApiData);
        }
      } catch (error) {
        console.error('Error updating negotiation status:', error);
        // Revert to original state if API call fails
        loadNegotiations();
      }
    } else if (over.id !== active.id && !over.id.startsWith('column-')) {
      // Se arrastou para reordenar na mesma coluna
      const oldIndex = negotiations.findIndex(item => item._id === active.id);
      const newIndex = negotiations.findIndex(item => item._id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(negotiations, oldIndex, newIndex);
        setNegotiations(newOrder);
        
        try {
          await api.put('/negotiations/reorder', {
            negotiationId: active.id,
            newPosition: newIndex,
          });
          console.log("API call successful - card reordered");
        } catch (error) {
          console.error('Error updating negotiation order:', error);
          loadNegotiations();
        }
      }
    }
  };

  const columns = [
    { id: 'new', title: t('kanban.columns.new'), color: 'bg-blue-100', stage: 'recebido' },
    { id: 'contacted', title: t('kanban.columns.contacted'), color: 'bg-yellow-100', stage: 'fechar' },
    { id: 'negotiating', title: t('kanban.columns.negotiating'), color: 'bg-purple-100', stage: 'negociacao' },
    { id: 'closed', title: t('kanban.columns.closed'), color: 'bg-green-100', stage: 'ganho' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('kanban.title')}
        description={t('kanban.description')}
      />

      {/* Barra de busca por placa e botão Nova negociação */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder={t('kanban.search.placeholder')}
              value={searchPlate}
              onChange={(e) => setSearchPlate(e.target.value)}
              className="pl-10"
            />
          </div>
          {searchPlate && (
            <>
              <span className="text-sm text-muted-foreground">
                {t('kanban.search.results', { 
                  filtered: filteredNegotiations.length, 
                  total: negotiations.length 
                })}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearchPlate('')}
                className="text-muted-foreground hover:text-foreground"
              >
                {t('kanban.search.clear')}
              </Button>
            </>
          )}
        </div>
        
        <Button 
          onClick={() => handleOpenModal()} 
          className="flex items-center gap-2"
        >
          <PlusCircle className="h-4 w-4" />
          {t('kanban.actions.newNegotiation')}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-40">
          <Spinner />
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
          modifiers={[restrictToWindowEdges]}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {columns.map((column) => (
              <div key={column.id} data-column-id={column.id}>
                <KanbanColumn
                  column={column}
                  items={filteredNegotiations.filter(item => item.status === column.id)}
                  onAddCard={(id) => handleOpenModal(id)}
                />
              </div>
            ))}
          </div>
          <DragOverlay dropAnimation={null}>
            {activeId && negotiations.find(n => n._id === activeId) ? (
              <SortableItem
                id={activeId}
                item={negotiations.find(n => n._id === activeId)}
                isDragging
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
      
      {isModalOpen && (
        <Dialog open={isModalOpen} onOpenChange={(open) => !open && handleCloseModal()}>
          <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
            <NegotiationModal 
              negotiationId={selectedNegotiation} 
              onSave={(shouldRefresh) => handleCloseModal(shouldRefresh !== false)}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
